/* Слои поверх экрана: выезжающая панель (drawer) и модалка. Стек: подтверждение открывается поверх карточки.
   Esc закрывает верхний слой, фокус удерживается внутри и возвращается на прежний элемент. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

interface LayerEntry {
  id: number;
  node: ReactNode;
}

interface LayersApi {
  /** Открыть слой поверх текущих; возвращает функцию закрытия. */
  push: (node: ReactNode) => () => void;
  /** Заменить все слои одним (как в макете: новая карточка вместо старой). */
  replace: (node: ReactNode) => () => void;
  closeTop: () => void;
  closeAll: () => void;
  depth: number;
}

const LayersCtx = createContext<LayersApi | null>(null);
const LayerIdCtx = createContext<number>(0);
/** Глубина слоя в стеке: каждый следующий слой — в своём контексте наложения поверх предыдущего. */
const LayerDepthCtx = createContext<number>(0);

export function useLayers(): LayersApi {
  const v = useContext(LayersCtx);
  if (!v) throw new Error("useLayers вне LayersProvider");
  return v;
}

/** Закрыть слой, в котором находится компонент. */
export function useCloseLayer(): () => void {
  const id = useContext(LayerIdCtx);
  const { closeTop } = useLayers();
  const ctx = useContext(LayersCtx) as LayersApi & { closeId?: (id: number) => void };
  return useCallback(() => (ctx.closeId ? ctx.closeId(id) : closeTop()), [ctx, id, closeTop]);
}

let seq = 1;

export function LayersProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<LayerEntry[]>([]);
  const focusStack = useRef<(Element | null)[]>([]);

  const closeId = useCallback((id: number) => {
    setStack((s) => s.filter((x) => x.id !== id));
    const prev = focusStack.current.pop();
    if (prev instanceof HTMLElement && document.contains(prev)) setTimeout(() => prev.focus(), 0);
  }, []);

  const push = useCallback(
    (node: ReactNode) => {
      const id = seq++;
      focusStack.current.push(document.activeElement);
      setStack((s) => [...s, { id, node }]);
      return () => closeId(id);
    },
    [closeId],
  );

  const replace = useCallback(
    (node: ReactNode) => {
      const id = seq++;
      if (!focusStack.current.length) focusStack.current.push(document.activeElement);
      else focusStack.current = focusStack.current.slice(0, 1);
      setStack([{ id, node }]);
      return () => closeId(id);
    },
    [closeId],
  );

  const closeTop = useCallback(() => {
    setStack((s) => {
      const top = s[s.length - 1];
      if (top) setTimeout(() => closeId(top.id), 0);
      return s;
    });
  }, [closeId]);

  const closeAll = useCallback(() => {
    setStack([]);
    const first = focusStack.current[0];
    focusStack.current = [];
    if (first instanceof HTMLElement && document.contains(first)) first.focus();
  }, []);

  useEffect(() => {
    if (!stack.length) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeId(stack[stack.length - 1].id);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [stack, closeId]);

  const api = useMemo(() => ({ push, replace, closeTop, closeAll, closeId, depth: stack.length }), [push, replace, closeTop, closeAll, closeId, stack.length]);

  return (
    <LayersCtx.Provider value={api}>
      {children}
      {stack.map((l, i) => (
        <LayerIdCtx.Provider key={l.id} value={l.id}>
          <LayerDepthCtx.Provider value={i}>{l.node}</LayerDepthCtx.Provider>
        </LayerIdCtx.Provider>
      ))}
    </LayersCtx.Provider>
  );
}

function useFocusTrap(ref: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const auto = el.querySelector<HTMLElement>("[autofocus]") || el.querySelector<HTMLElement>(".btn-close");
    // React autoFocus не ставит атрибут, но фокус уже внутри — тогда не перехватываем
    const t = setTimeout(() => {
      if (!el.contains(document.activeElement)) auto?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const f = el.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    };
    el.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      el.removeEventListener("keydown", onKey);
    };
  }, [ref]);
}

interface PanelProps {
  title: ReactNode;
  sub?: ReactNode;
  headExtra?: ReactNode;
  foot?: ReactNode;
  children: ReactNode;
  label?: string;
}

function Head({ title, sub, headExtra, onClose }: { title: ReactNode; sub?: ReactNode; headExtra?: ReactNode; onClose: () => void }) {
  return (
    <div className="drawer-head">
      <div className="titles">
        <h2>{title}</h2>
        {sub ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {sub}
          </div>
        ) : null}
      </div>
      {headExtra}
      <button className="btn btn-ghost btn-icon btn-close" onClick={onClose} aria-label="Закрыть">
        <Icon name="x" />
      </button>
    </div>
  );
}

/** Выезжающая справа панель (карточка заказа, клиента, кампании). */
export function Drawer({ title, sub, headExtra, foot, children, label }: PanelProps) {
  const close = useCloseLayer();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref);
  const depth = useContext(LayerDepthCtx);
  return createPortal(
    <div style={{ position: "relative", zIndex: 50 + depth }}>
      <div className="overlay" onClick={close} />
      <div ref={ref} className="drawer" role="dialog" aria-modal="true" aria-label={label || (typeof title === "string" ? title : "")}>
        <Head title={title} sub={sub} headExtra={headExtra} onClose={close} />
        <div className="drawer-body">{children}</div>
        {foot ? <div className="drawer-foot">{foot}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** Модальное окно по центру. */
export function Modal({ title, sub, foot, children, small, label }: PanelProps & { small?: boolean }) {
  const close = useCloseLayer();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref);
  const depth = useContext(LayerDepthCtx);
  return createPortal(
    <div style={{ position: "relative", zIndex: 50 + depth }}>
      <div className="overlay" onClick={close} />
      <div ref={ref} className={"modal" + (small ? " modal-sm" : "")} role="dialog" aria-modal="true" aria-label={label || (typeof title === "string" ? title : "")}>
        <Head title={title} sub={sub} onClose={close} />
        <div className="drawer-body">{children}</div>
        {foot ? <div className="drawer-foot">{foot}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/* ---------- подтверждение вместо window.confirm ---------- */

export interface ConfirmOptions {
  title: string;
  text: ReactNode;
  ok: string;
  danger?: boolean;
  /** Выбор причины (отмена, неявка) — значение вернётся вместо true. */
  reasons?: { label: string; options: string[] };
}

function ConfirmBody({ opts, resolve }: { opts: ConfirmOptions; resolve: (v: string | boolean) => void }) {
  const close = useCloseLayer();
  const [reason, setReason] = useState(opts.reasons?.options[0] || "");
  const done = (v: string | boolean) => {
    resolve(v);
    close();
  };
  useEffect(() => () => resolve(false), [resolve]);
  return (
    <Modal
      small
      title={opts.title}
      foot={
        <>
          <button className="btn" onClick={() => done(false)}>
            Отмена
          </button>
          <button className={"btn " + (opts.danger ? "btn-danger" : "btn-primary")} autoFocus onClick={() => done(opts.reasons ? reason : true)}>
            {opts.ok}
          </button>
        </>
      }
    >
      <p>{opts.text}</p>
      {opts.reasons ? (
        <label className="field">
          <span>{opts.reasons.label}</span>
          <select className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
            {opts.reasons.options.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
      ) : null}
    </Modal>
  );
}

/** Подтверждение: true / причина — если подтвердили, false — если отказались. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<string | boolean> {
  const { push } = useLayers();
  return useCallback(
    (opts: ConfirmOptions) =>
      new Promise<string | boolean>((resolve) => {
        let settled = false;
        const once = (v: string | boolean) => {
          if (settled) return;
          settled = true;
          resolve(v);
        };
        push(<ConfirmBody opts={opts} resolve={once} />);
      }),
    [push],
  );
}
